import { GLOBAL_FILTERS_KEY } from './constants';

export type GlobalFiltersPartial = Partial<{
    companyIds: number[];
    dateFrom: string;
    dateTo: string;
}>;

type FoundationCompanies = {
    companies: Array<{ id: number; code: string }>;
};

export function defaultAdoCompanyIds(foundation: FoundationCompanies): number[] {
    const ado = foundation.companies.find((c) => c.code.toUpperCase() === 'ADO');
    if (ado) {
        return [ado.id];
    }
    const first = foundation.companies[0];

    return first ? [first.id] : [];
}

export function previousQuarterRangeIso(today = new Date()): { dateFrom: string; dateTo: string } {
    const t = new Date(today);
    t.setHours(12, 0, 0, 0);
    const year = t.getFullYear();
    const quarter = Math.floor(t.getMonth() / 3);
    const prevQuarter = quarter === 0 ? 3 : quarter - 1;
    const prevQuarterYear = quarter === 0 ? year - 1 : year;
    const startMonth = prevQuarter * 3;
    const start = new Date(prevQuarterYear, startMonth, 1, 12, 0, 0, 0);
    const end = new Date(prevQuarterYear, startMonth + 3, 0, 12, 0, 0, 0);
    const toIso = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    return { dateFrom: toIso(start), dateTo: toIso(end) };
}

export function readStoredGlobalFilters(foundation: { companies: Array<{ id: number }> }): GlobalFiltersPartial {
    try {
        const raw = localStorage.getItem(GLOBAL_FILTERS_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') {
            return {};
        }
        const obj = parsed as Record<string, unknown>;
        const next: GlobalFiltersPartial = {};
        if (Array.isArray(obj.companyIds)) {
            const companyIds = obj.companyIds
                .map((v) => Number(v))
                .filter((id) => foundation.companies.some((c) => c.id === id));
            if (companyIds.length > 0) {
                next.companyIds = companyIds;
            }
        }
        if (typeof obj.dateFrom === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(obj.dateFrom)) {
            next.dateFrom = obj.dateFrom;
        }
        if (typeof obj.dateTo === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(obj.dateTo)) {
            next.dateTo = obj.dateTo;
        }

        return next;
    } catch {
        return {};
    }
}

export function normalizeNumberArray(value: unknown): number[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((item) => Number(item))
        .filter((item, index, all) => Number.isFinite(item) && all.indexOf(item) === index);
}

export function canOpenAdmin(user: { role: string; allowed_modules?: string[] }): boolean {
    if (user.role === 'superadministrator' || user.role === 'administrator') {
        return true;
    }
    // Ne-administratori: ulaz u Administraciju ako im je preko modula 104 dodijeljen
    // barem jedan granularan ACL ključ za neku admin sekciju (npr. m.110, m.121…).
    const a = user.allowed_modules;
    if (! a || a.length === 0) {
        return false;
    }
    if (a.includes('admin') || a.includes('m.100')) {
        return true;
    }

    return a.some((key) => typeof key === 'string' && key.startsWith('m.'));
}
